import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createProduct, fetchCompare, fetchProduct, fetchProducts, fetchReviews, submitReview, triggerEnrichment, type ReviewSubmission } from "@lib/api-client";

export function useProducts() {
  return useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts()
  });
}

export function useProductDetail(slug: string) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: () => fetchProduct(slug),
    enabled: Boolean(slug)
  });
}

export function useReviews(productId: string) {
  return useQuery({
    queryKey: ["reviews", productId],
    queryFn: () => fetchReviews(productId),
    enabled: Boolean(productId)
  });
}

export function useReviewSubmission(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReviewSubmission) => submitReview(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reviews", productId] });
    }
  });
}

export function useCompare(ids: string) {
  return useQuery({
    queryKey: ["compare", ids],
    queryFn: () => fetchCompare(ids),
    enabled: ids.split(",").filter(Boolean).length === 2
  });
}


export function useAdminProducts() {
  return useQuery({
    queryKey: ["admin-products"],
    queryFn: () => fetchProducts({ includeAll: true })
  });
}

export function useCreateProductMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    }
  });
}

export function useTriggerEnrichmentMutation() {
  return useMutation({
    mutationFn: () => triggerEnrichment()
  });
}
